(() => {
  const selector = "[data-smartpost-shipping]";
  const lockersUrl = "https://partner.ehestu.ee/static/store/js/parcel-lockers.js";
  const deliveryCountriesUrl = "https://partner.ehestu.ee/api/public/deliveryCountries";
  const fallbackDeliveryCountry = "EE";
  const deliveryCountriesRequestTimeout = 3000;
  const lockerListMaxHeight = 240;
  const lockerVirtualItemHeight = 56;
  const lockerVirtualOverscan = 6;

  let lockersPromise;
  let deliveryCountriesPromise;
  const checkoutButtonOriginalDisabled = new WeakMap();

  const normalizeDeliveryCountries = (countries) => {
    if (!Array.isArray(countries)) {
      return [];
    }

    return countries.reduce((normalizedCountries, country) => {
      const countryCode = country === null || country === undefined ? "" : String(country).trim().toUpperCase();

      if (/^[A-Z]{2}$/.test(countryCode) && normalizedCountries.indexOf(countryCode) === -1) {
        normalizedCountries.push(countryCode);
      }

      return normalizedCountries;
    }, []);
  };

  const loadDeliveryCountries = () => {
    if (deliveryCountriesPromise) {
      return deliveryCountriesPromise;
    }

    const abortController = window.AbortController ? new AbortController() : null;
    const timeout = abortController ? window.setTimeout(() => abortController.abort(), deliveryCountriesRequestTimeout) : null;

    deliveryCountriesPromise = fetch(deliveryCountriesUrl, {
      headers: { Accept: "application/json" },
      signal: abortController ? abortController.signal : undefined
    }).then((response) => {
      if (!response.ok) {
        throw new Error("Delivery countries request failed");
      }

      return response.json();
    }).then((countries) => {
      const normalizedCountries = normalizeDeliveryCountries(countries);

      return normalizedCountries.length > 0 ? normalizedCountries : [fallbackDeliveryCountry];
    }).catch(() => [fallbackDeliveryCountry]).finally(() => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
    });

    return deliveryCountriesPromise;
  };

  const loadParcelLockers = () => {
    if (window.parcelLockers) {
      return Promise.resolve(window.parcelLockers);
    }

    if (lockersPromise) {
      return lockersPromise;
    }

    lockersPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(`script[src="${lockersUrl}"]`);
      const resolveParcelLockers = () => {
        if (window.parcelLockers) {
          resolve(window.parcelLockers);
          return;
        }

        reject(new Error("Parcel locker data not found"));
      };

      if (existingScript) {
        if (existingScript.dataset.smartpostLoaded === "true") {
          resolveParcelLockers();
          return;
        }

        existingScript.addEventListener("load", resolveParcelLockers, { once: true });
        existingScript.addEventListener("error", reject, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = lockersUrl;
      script.onload = () => {
        script.dataset.smartpostLoaded = "true";
        resolveParcelLockers();
      };
      script.onerror = reject;
      document.body.appendChild(script);
    });

    return lockersPromise;
  };

  const updateCartAttributes = (country, locker, lockerName = "") => {
    const cartUpdateUrl = window.Shopify && Shopify.routes && Shopify.routes.root ? `${Shopify.routes.root}cart/update.js` : "/cart/update.js";

    fetch(cartUpdateUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          PLACE_COUNTRY: country,
          PLACE_ID: locker,
          PLACE_NAME: locker ? lockerName : ""
        }
      }),
      keepalive: true
    }).catch(() => {});
  };

  const resetOptions = (select, text) => {
    while (select.firstChild) {
      select.removeChild(select.firstChild);
    }

    select.appendChild(new Option(text, ""));
  };

  const countryHasLockers = (country) => {
    const countryLockers = window.parcelLockers && window.parcelLockers[country];

    return Boolean(countryLockers && Object.keys(countryLockers).some((city) => {
      return Array.isArray(countryLockers[city]) && countryLockers[city].length > 0;
    }));
  };

  const setHiddenLockerSelectValue = (lockerSelect, lockerOption) => {
    const emptyText = lockerSelect.options.length > 0 ? lockerSelect.options[0].textContent : "";

    resetOptions(lockerSelect, emptyText);

    if (!lockerOption) {
      lockerSelect.value = "";
      return;
    }

    lockerSelect.appendChild(new Option(lockerOption.name, lockerOption.id));
    lockerSelect.value = lockerOption.id;
  };

  const closestForm = (element) => {
    let currentElement = element;

    while (currentElement) {
      if (currentElement.tagName === "FORM") {
        return currentElement;
      }

      currentElement = currentElement.parentElement;
    }

    return null;
  };

  const getCheckoutButtons = (root) => {
    const form = closestForm(root);

    return form ? form.querySelectorAll('button[type="submit"][name="checkout"]') : [];
  };

  const setCheckoutButtonsLoading = (root, isLoading) => {
    getCheckoutButtons(root).forEach((button) => {
      if (isLoading) {
        if (!checkoutButtonOriginalDisabled.has(button)) {
          checkoutButtonOriginalDisabled.set(button, button.disabled);
        }

        button.disabled = true;
        return;
      }

      if (checkoutButtonOriginalDisabled.has(button)) {
        button.disabled = checkoutButtonOriginalDisabled.get(button);
        checkoutButtonOriginalDisabled.delete(button);
      }
    });
  };

  const setSmartpostLoadState = (root, state) => {
    root.dataset.smartpostLoadState = state;
    setCheckoutButtonsLoading(root, state === "loading");
  };

  const getInvalidSmartpostControl = (root) => {
    const countrySelect = root.querySelector("[data-smartpost-country-select]");
    const lockerGroup = root.querySelector("[data-smartpost-locker-group]");
    const lockerComboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const lockerInput = root.querySelector("[data-smartpost-locker-input]");

    if (root.dataset.smartpostLoadState === "failed") {
      return null;
    }

    if (!countrySelect.value) {
      return countrySelect;
    }

    if (!lockerGroup.hidden && lockerComboboxInput.required && !lockerInput.value) {
      return lockerComboboxInput;
    }

    return null;
  };

  const setCountrySelectVisibility = (root, shouldShow) => {
    const countryGroup = root.querySelector("[data-smartpost-country-group]");
    const countrySelect = root.querySelector("[data-smartpost-country-select]");

    if (countryGroup) {
      countryGroup.hidden = !shouldShow;
    }

    if (countrySelect) {
      countrySelect.required = shouldShow;
    }
  };

  const setFallbackCountry = (root, countryEmptyText, countryNames, regionNames) => {
    const countrySelect = root.querySelector("[data-smartpost-country-select]");
    const countryInput = root.querySelector("[data-smartpost-country-input]");

    resetOptions(countrySelect, countryEmptyText);
    countrySelect.appendChild(new Option(getCountryLabel(fallbackDeliveryCountry, countryNames, regionNames), fallbackDeliveryCountry));
    countrySelect.value = fallbackDeliveryCountry;
    countryInput.value = fallbackDeliveryCountry;
    setCountrySelectVisibility(root, false);
  };

  const reportInvalidControl = (control) => {
    if (typeof control.reportValidity === "function") {
      control.reportValidity();
      return;
    }

    control.focus();
  };

  const attachSmartpostFormGuard = (root) => {
    const form = closestForm(root);

    if (!form || form.dataset.smartpostGuardInitialized === "true") {
      return;
    }

    form.dataset.smartpostGuardInitialized = "true";

    form.addEventListener("submit", (event) => {
      const formSelector = form.querySelector(selector);

      if (formSelector && formSelector.dataset.smartpostLoadState === "loading") {
        event.preventDefault();
        return;
      }

      const invalidControl = formSelector ? getInvalidSmartpostControl(formSelector) : null;

      if (!invalidControl) {
        return;
      }

      event.preventDefault();
      reportInvalidControl(invalidControl);
    });
  };

  const getCountryLabel = (country, countryNames, regionNames) => {
    if (countryNames[country]) {
      return countryNames[country];
    }

    try {
      return regionNames ? regionNames.of(country) || country : country;
    } catch (error) {
      return country;
    }
  };

  const setComboboxExpanded = (input, listbox, expanded) => {
    input.setAttribute("aria-expanded", expanded ? "true" : "false");
    listbox.hidden = !expanded;
  };

  const updateLockerListboxPosition = (root) => {
    const combobox = root.querySelector("[data-smartpost-locker-combobox]");
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const listbox = root.querySelector("[data-smartpost-locker-listbox]");

    if (!combobox || !comboboxInput || !listbox || listbox.hidden) {
      return;
    }

    const viewportPadding = 12;
    const inputRect = comboboxInput.getBoundingClientRect();
    const spaceBelow = window.innerHeight - inputRect.bottom - viewportPadding;
    const spaceAbove = inputRect.top - viewportPadding;
    const preferredHeight = Math.min(listbox.scrollHeight || lockerListMaxHeight, lockerListMaxHeight);
    const shouldOpenAbove = preferredHeight > spaceBelow && spaceAbove > spaceBelow;
    const availableSpace = shouldOpenAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(0, Math.min(lockerListMaxHeight, availableSpace));

    combobox.classList.toggle("smartpost-combobox--above", shouldOpenAbove);
    listbox.style.maxHeight = `${maxHeight}px`;
  };

  const setLockerValidity = (root) => {
    const lockerGroup = root.querySelector("[data-smartpost-locker-group]");
    const lockerInput = root.querySelector("[data-smartpost-locker-input]");
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");

    if (!comboboxInput) {
      return;
    }

    if (lockerGroup.hidden || !comboboxInput.required || lockerInput.value) {
      comboboxInput.setCustomValidity("");
      return;
    }

    comboboxInput.setCustomValidity(comboboxInput.placeholder || "");
  };

  const closeLockerListbox = (root) => {
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const listbox = root.querySelector("[data-smartpost-locker-listbox]");

    if (!comboboxInput || !listbox) {
      return;
    }

    root.smartpostActiveLockerIndex = -1;
    listbox.style.maxHeight = "";
    const combobox = listbox.closest("[data-smartpost-locker-combobox]");

    if (combobox) {
      combobox.classList.remove("smartpost-combobox--above");
    }

    comboboxInput.removeAttribute("aria-activedescendant");
    setComboboxExpanded(comboboxInput, listbox, false);
  };

  const normalizeSearchText = (value) => {
    return value ? value.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase() : "";
  };

  const filterLockerOptions = (options, query) => {
    const normalizedQuery = normalizeSearchText(query).trim();

    if (!normalizedQuery) {
      return options;
    }

    const primaryMatches = [];
    const secondaryMatches = [];

    options.forEach((option) => {
      if (option.primarySearchText.indexOf(normalizedQuery) !== -1) {
        primaryMatches.push(option);
        return;
      }

      if (option.secondarySearchText.indexOf(normalizedQuery) !== -1) {
        secondaryMatches.push(option);
      }
    });

    return primaryMatches.concat(secondaryMatches);
  };

  const clearLockerListbox = (listbox) => {
    while (listbox.firstChild) {
      listbox.removeChild(listbox.firstChild);
    }
  };

  const getLockerOptionId = (listbox, index) => `${listbox.id}-option-${index}`;

  const getLockerVirtualItemHeight = (root) => {
    const listbox = root.querySelector("[data-smartpost-locker-listbox]");
    const configuredHeight = listbox ? parseFloat(window.getComputedStyle(listbox).getPropertyValue("--smartpost-option-height")) : NaN;

    return isFinite(configuredHeight) && configuredHeight > 0 ? configuredHeight : lockerVirtualItemHeight;
  };

  const appendLockerVirtualSpacer = (listbox, height) => {
    if (height <= 0) {
      return;
    }

    const spacer = document.createElement("li");
    spacer.className = "smartpost-combobox__spacer";
    spacer.setAttribute("aria-hidden", "true");
    spacer.setAttribute("role", "presentation");
    spacer.style.height = `${height}px`;
    listbox.appendChild(spacer);
  };

  const createLockerOptionElement = (root, listbox, lockerOption, index) => {
    const item = document.createElement("li");
    const optionName = document.createElement("span");
    const optionCity = document.createElement("span");

    item.id = getLockerOptionId(listbox, index);
    item.className = "smartpost-combobox__option";
    item.dataset.value = lockerOption.id;
    item.dataset.smartpostLockerOptionIndex = index.toString();
    item.setAttribute("role", "option");
    item.setAttribute("aria-selected", root.smartpostActiveLockerIndex === index ? "true" : "false");

    optionName.className = "smartpost-combobox__option-name";
    optionName.textContent = lockerOption.name;
    optionCity.className = "smartpost-combobox__option-city";
    optionCity.textContent = lockerOption.city;

    item.append(optionName, optionCity);

    return item;
  };

  const renderVisibleLockerOptions = (root) => {
    const listbox = root.querySelector("[data-smartpost-locker-listbox]");
    const options = root.smartpostVisibleLockerOptions || [];

    if (!listbox || options.length === 0) {
      return;
    }

    const itemHeight = getLockerVirtualItemHeight(root);
    const viewportHeight = listbox.clientHeight || parseFloat(listbox.style.maxHeight) || lockerListMaxHeight;
    const firstVisibleIndex = Math.max(0, Math.floor(listbox.scrollTop / itemHeight) - lockerVirtualOverscan);
    const renderedCount = Math.ceil(viewportHeight / itemHeight) + lockerVirtualOverscan * 2;
    const lastVisibleIndex = Math.min(options.length - 1, firstVisibleIndex + renderedCount - 1);

    if (
      root.smartpostRenderedLockerStart === firstVisibleIndex &&
      root.smartpostRenderedLockerEnd === lastVisibleIndex &&
      root.smartpostRenderedLockerCount === options.length &&
      root.smartpostRenderedLockerActive === root.smartpostActiveLockerIndex
    ) {
      return;
    }

    root.smartpostRenderedLockerStart = firstVisibleIndex;
    root.smartpostRenderedLockerEnd = lastVisibleIndex;
    root.smartpostRenderedLockerCount = options.length;
    root.smartpostRenderedLockerActive = root.smartpostActiveLockerIndex;

    clearLockerListbox(listbox);
    appendLockerVirtualSpacer(listbox, firstVisibleIndex * itemHeight);

    for (let index = firstVisibleIndex; index <= lastVisibleIndex; index += 1) {
      listbox.appendChild(createLockerOptionElement(root, listbox, options[index], index));
    }

    appendLockerVirtualSpacer(listbox, (options.length - lastVisibleIndex - 1) * itemHeight);
  };

  const updateActiveLockerOption = (root, index) => {
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const listbox = root.querySelector("[data-smartpost-locker-listbox]");
    const options = root.smartpostVisibleLockerOptions || [];

    if (!comboboxInput || !listbox || options.length === 0) {
      return;
    }

    const nextIndex = Math.max(0, Math.min(index, options.length - 1));
    const itemHeight = getLockerVirtualItemHeight(root);
    const viewportHeight = listbox.clientHeight || parseFloat(listbox.style.maxHeight) || lockerListMaxHeight;
    const optionTop = nextIndex * itemHeight;
    const optionBottom = optionTop + itemHeight;

    root.smartpostActiveLockerIndex = nextIndex;

    if (optionTop < listbox.scrollTop) {
      listbox.scrollTop = optionTop;
    } else if (optionBottom > listbox.scrollTop + viewportHeight) {
      listbox.scrollTop = optionBottom - viewportHeight;
    }

    renderVisibleLockerOptions(root);
    comboboxInput.setAttribute("aria-activedescendant", getLockerOptionId(listbox, nextIndex));
  };

  const selectLockerOption = (root, lockerOption, shouldUpdateCart = true) => {
    const countryInput = root.querySelector("[data-smartpost-country-input]");
    const lockerSelect = root.querySelector("[data-smartpost-locker-select]");
    const lockerInput = root.querySelector("[data-smartpost-locker-input]");
    const lockerNameInput = root.querySelector("[data-smartpost-locker-name-input]");
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");

    setHiddenLockerSelectValue(lockerSelect, lockerOption);
    lockerInput.value = lockerOption.id;
    lockerNameInput.value = lockerOption.name;
    comboboxInput.value = lockerOption.name;
    comboboxInput.dataset.selectedValue = lockerOption.id;
    setLockerValidity(root);
    closeLockerListbox(root);

    if (shouldUpdateCart) {
      updateCartAttributes(countryInput.value, lockerOption.id, lockerOption.name);
    }
  };

  const renderLockerListbox = (root) => {
    const combobox = root.querySelector("[data-smartpost-locker-combobox]");
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const listbox = root.querySelector("[data-smartpost-locker-listbox]");
    const options = filterLockerOptions(root.smartpostLockerOptions || [], comboboxInput.value);

    clearLockerListbox(listbox);

    root.smartpostVisibleLockerOptions = options;
    root.smartpostActiveLockerIndex = -1;
    root.smartpostRenderedLockerStart = null;
    root.smartpostRenderedLockerEnd = null;
    root.smartpostRenderedLockerCount = null;
    root.smartpostRenderedLockerActive = null;
    listbox.scrollTop = 0;
    comboboxInput.removeAttribute("aria-activedescendant");

    if (options.length === 0) {
      const emptyItem = document.createElement("li");
      emptyItem.className = "smartpost-combobox__empty";
      emptyItem.textContent = combobox.dataset.noResultsText || "";
      listbox.appendChild(emptyItem);
      return;
    }

    renderVisibleLockerOptions(root);
  };

  const openLockerListbox = (root) => {
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const listbox = root.querySelector("[data-smartpost-locker-listbox]");

    if (!comboboxInput || comboboxInput.disabled) {
      return;
    }

    renderLockerListbox(root);
    setComboboxExpanded(comboboxInput, listbox, true);
    updateLockerListboxPosition(root);
    renderVisibleLockerOptions(root);
  };

  const clearSelectedLocker = (root, shouldUpdateCart = false) => {
    const countryInput = root.querySelector("[data-smartpost-country-input]");
    const lockerSelect = root.querySelector("[data-smartpost-locker-select]");
    const lockerInput = root.querySelector("[data-smartpost-locker-input]");
    const lockerNameInput = root.querySelector("[data-smartpost-locker-name-input]");
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const hadSelectedLocker = Boolean(lockerInput.value);

    lockerSelect.value = "";
    lockerInput.value = "";
    lockerNameInput.value = "";
    comboboxInput.dataset.selectedValue = "";
    setLockerValidity(root);

    if (shouldUpdateCart && hadSelectedLocker) {
      updateCartAttributes(countryInput.value, "");
    }
  };

  const attachLockerCombobox = (root) => {
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const listbox = root.querySelector("[data-smartpost-locker-listbox]");

    if (!comboboxInput || !listbox) {
      return;
    }

    comboboxInput.addEventListener("click", () => openLockerListbox(root));

    listbox.addEventListener("mousedown", (event) => {
      const optionElement = event.target instanceof Element ? event.target.closest("[data-smartpost-locker-option-index]") : null;

      if (optionElement) {
        event.preventDefault();
      }
    });

    listbox.addEventListener("click", (event) => {
      const optionElement = event.target instanceof Element ? event.target.closest("[data-smartpost-locker-option-index]") : null;

      if (!optionElement || !listbox.contains(optionElement)) {
        return;
      }

      const lockerOption = (root.smartpostVisibleLockerOptions || [])[parseInt(optionElement.dataset.smartpostLockerOptionIndex, 10)];

      if (lockerOption) {
        selectLockerOption(root, lockerOption);
      }
    });

    listbox.addEventListener("scroll", () => {
      if (listbox.hidden || root.smartpostLockerScrollFrame) {
        return;
      }

      const renderOnFrame = () => {
        root.smartpostLockerScrollFrame = null;
        renderVisibleLockerOptions(root);
      };

      root.smartpostLockerScrollFrame = window.requestAnimationFrame ? window.requestAnimationFrame(renderOnFrame) : window.setTimeout(renderOnFrame, 16);
    });

    comboboxInput.addEventListener("input", () => {
      clearSelectedLocker(root, true);
      openLockerListbox(root);
    });

    comboboxInput.addEventListener("keydown", (event) => {
      const options = root.smartpostVisibleLockerOptions || [];

      if (event.key === "ArrowDown") {
        event.preventDefault();

        if (listbox.hidden) {
          openLockerListbox(root);
          updateActiveLockerOption(root, 0);
          return;
        }

        updateActiveLockerOption(root, root.smartpostActiveLockerIndex === -1 ? 0 : root.smartpostActiveLockerIndex + 1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();

        if (listbox.hidden) {
          openLockerListbox(root);
          updateActiveLockerOption(root, options.length - 1);
          return;
        }

        updateActiveLockerOption(root, root.smartpostActiveLockerIndex === -1 ? options.length - 1 : root.smartpostActiveLockerIndex - 1);
      } else if (event.key === "Enter" && !listbox.hidden) {
        const selectedOption = options[root.smartpostActiveLockerIndex];

        if (selectedOption) {
          event.preventDefault();
          selectLockerOption(root, selectedOption);
        }
      } else if (event.key === "Escape") {
        closeLockerListbox(root);
      } else if (event.key === "Tab") {
        closeLockerListbox(root);
      }
    });

    comboboxInput.addEventListener("blur", () => setLockerValidity(root));

    document.addEventListener("click", (event) => {
      if (!root.contains(event.target)) {
        closeLockerListbox(root);
      }
    });

    window.addEventListener("resize", () => updateLockerListboxPosition(root));
    window.addEventListener("scroll", () => updateLockerListboxPosition(root), true);
  };

  const setLockerOptions = (root, country, selectedLocker) => {
    const lockerGroup = root.querySelector("[data-smartpost-locker-group]");
    const lockerSelect = root.querySelector("[data-smartpost-locker-select]");
    const lockerInput = root.querySelector("[data-smartpost-locker-input]");
    const lockerNameInput = root.querySelector("[data-smartpost-locker-name-input]");
    const comboboxInput = root.querySelector("[data-smartpost-locker-combobox-input]");
    const emptyText = lockerSelect.options.length > 0 ? lockerSelect.options[0].textContent : "";
    const countryLockers = window.parcelLockers && window.parcelLockers[country];
    const lockerOptions = [];

    resetOptions(lockerSelect, emptyText);
    lockerInput.value = "";
    lockerNameInput.value = "";
    comboboxInput.value = "";
    comboboxInput.dataset.selectedValue = "";
    comboboxInput.disabled = true;
    comboboxInput.required = false;
    root.smartpostLockerOptions = lockerOptions;
    closeLockerListbox(root);

    if (!country || !countryHasLockers(country)) {
      lockerSelect.required = false;
      lockerGroup.hidden = true;
      setLockerValidity(root);
      return;
    }

    Object.keys(countryLockers).sort().forEach((city) => {
      countryLockers[city].forEach((locker) => {
        const lockerFieldText = Object.keys(locker).map((key) => locker[key]).filter((value) => {
          return typeof value === "string" || typeof value === "number";
        }).join(" ");

        lockerOptions.push({
          id: locker.id.toString(),
          name: locker.name,
          city,
          primarySearchText: normalizeSearchText(`${city} ${locker.name}`),
          secondarySearchText: normalizeSearchText(lockerFieldText)
        });
      });
    });

    root.smartpostLockerOptions = lockerOptions;
    lockerGroup.hidden = false;
    lockerSelect.required = false;
    comboboxInput.disabled = false;
    comboboxInput.required = true;

    if (selectedLocker) {
      const selectedLockerOption = lockerOptions.find((lockerOption) => lockerOption.id === selectedLocker);

      if (selectedLockerOption) {
        selectLockerOption(root, selectedLockerOption, false);
      }
    }

    setLockerValidity(root);
  };

  const initializeSmartpostSelector = async (root) => {
    if (root.dataset.smartpostInitialized === "true") {
      return;
    }

    root.dataset.smartpostInitialized = "true";

    const countrySelect = root.querySelector("[data-smartpost-country-select]");
    const lockerSelect = root.querySelector("[data-smartpost-locker-select]");
    const countryInput = root.querySelector("[data-smartpost-country-input]");
    const lockerInput = root.querySelector("[data-smartpost-locker-input]");
    const lockerNameInput = root.querySelector("[data-smartpost-locker-name-input]");
    const countryNames = JSON.parse(root.dataset.countryNames || "{}");
    const selectedCountry = root.dataset.selectedCountry || countryInput.value;
    const selectedLocker = root.dataset.selectedLocker || lockerInput.value;
    const selectedLockerName = root.dataset.selectedLockerName || lockerNameInput.value;
    const countryEmptyText = countrySelect.options.length > 0 ? countrySelect.options[0].textContent : "";
    const locale = window.Shopify && Shopify.locale ? Shopify.locale : document.documentElement.lang || "en";
    const regionNames = window.Intl && Intl.DisplayNames ? new Intl.DisplayNames([locale], { type: "region" }) : null;

    attachSmartpostFormGuard(root);
    attachLockerCombobox(root);
    setFallbackCountry(root, countryEmptyText, countryNames, regionNames);
    setSmartpostLoadState(root, "loading");

    try {
      const results = await Promise.all([loadParcelLockers(), loadDeliveryCountries()]);
      const enabledCountries = results[1];
      let visibleCountries = Array.from(new Set(enabledCountries)).filter(countryHasLockers).sort((firstCountry, secondCountry) => {
        return getCountryLabel(firstCountry, countryNames, regionNames).localeCompare(getCountryLabel(secondCountry, countryNames, regionNames), locale);
      });

      if (visibleCountries.length === 0) {
        visibleCountries = [fallbackDeliveryCountry];
      }

      resetOptions(countrySelect, countryEmptyText);

      visibleCountries.forEach((country) => {
        countrySelect.appendChild(new Option(getCountryLabel(country, countryNames, regionNames), country));
      });

      const effectiveCountry = selectedCountry && visibleCountries.indexOf(selectedCountry) !== -1 ? selectedCountry : visibleCountries[0];
      const effectiveSelectedLocker = effectiveCountry === selectedCountry ? selectedLocker : "";

      setCountrySelectVisibility(root, visibleCountries.length > 1);

      if (effectiveCountry) {
        countrySelect.value = effectiveCountry;
        countryInput.value = effectiveCountry;
        setLockerOptions(root, effectiveCountry, effectiveSelectedLocker);

        if (selectedCountry !== effectiveCountry || selectedLocker !== lockerInput.value || selectedLockerName !== lockerNameInput.value || !selectedCountry) {
          updateCartAttributes(effectiveCountry, lockerInput.value, lockerNameInput.value);
        }
      }

      setSmartpostLoadState(root, "ready");
    } catch (error) {
      console.error(error);
      setSmartpostLoadState(root, "failed");
      return;
    }

    countrySelect.addEventListener("change", () => {
      countryInput.value = countrySelect.value;
      lockerInput.value = "";
      lockerNameInput.value = "";
      setLockerOptions(root, countrySelect.value, "");
      updateCartAttributes(countrySelect.value, "");
    });

    lockerSelect.addEventListener("change", () => {
      const selectedLockerOption = (root.smartpostLockerOptions || []).find((lockerOption) => lockerOption.id === lockerSelect.value);

      if (selectedLockerOption) {
        selectLockerOption(root, selectedLockerOption);
      }
    });
  };

  const initializeSmartpostSelectors = (container = document) => {
    container.querySelectorAll(selector).forEach(initializeSmartpostSelector);
  };

  const observeSmartpostSelectors = () => {
    if (!window.MutationObserver || document.body.dataset.smartpostObserverInitialized === "true") {
      return;
    }

    document.body.dataset.smartpostObserverInitialized = "true";

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
          }

          if (node.matches && node.matches(selector)) {
            initializeSmartpostSelector(node);
            return;
          }

          if (node.querySelectorAll) {
            initializeSmartpostSelectors(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener("DOMContentLoaded", () => {
    initializeSmartpostSelectors();
    observeSmartpostSelectors();
  });
  document.addEventListener("cart-drawer:refreshed", (event) => initializeSmartpostSelectors(event.target));
  document.addEventListener("cart:refresh", () => initializeSmartpostSelectors());
})();
